import React from "react";
import { Box, ButtonBase, Typography, useTheme } from "@mui/material";

export interface MobileDashboardTabsProps {
  activeTab: number;
  onChangeTab: (tabIndex: number) => void;
  tab0Count?: number;
  tab1Count?: number;
}

export const MobileDashboardTabs: React.FC<MobileDashboardTabsProps> = ({
  activeTab,
  onChangeTab,
  tab0Count,
  tab1Count,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const tabs = [
    { label: "Overview", count: tab0Count },
    { label: "Upsell", count: tab1Count },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        my: 1.5,
      }}
    >
      <Box
        role="tablist"
        aria-label="Dashboard views"
        sx={{
          width: "100%",
          maxWidth: 440,
          p: 0.5,
          borderRadius: "28px",
          backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "#eef2f6",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 0.5,
          position: "relative",
          userSelect: "none",
        }}
      >
        {tabs.map((t, idx) => {
          const isActive = activeTab === idx;
          return (
            <ButtonBase
              key={t.label}
              role="tab"
              aria-selected={isActive}
              onClick={() => onChangeTab(idx)}
              sx={{
                minHeight: 46,
                borderRadius: "24px",
                py: 1,
                px: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                backgroundColor: isActive
                  ? isDark
                    ? theme.palette.primary.main
                    : "#ffffff"
                  : "transparent",
                color: isActive
                  ? isDark
                    ? "#ffffff"
                    : theme.palette.primary.main
                  : isDark
                  ? "rgba(255,255,255,0.6)"
                  : "#64748b",
                boxShadow: isActive
                  ? isDark
                    ? "0 4px 14px rgba(0,0,0,0.4)"
                    : "0 4px 12px rgba(0,0,0,0.08)"
                  : "none",
                fontWeight: isActive ? 700 : 500,
                "&:active": {
                  transform: "scale(0.97)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: isActive ? 700 : 600,
                  letterSpacing: "-0.01em",
                }}
              >
                {t.label}
              </Typography>
              {typeof t.count === "number" && t.count > 0 && (
                <Box
                  sx={{
                    px: 0.85,
                    py: 0.15,
                    borderRadius: "999px",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    backgroundColor: isActive
                      ? isDark
                        ? "rgba(255,255,255,0.25)"
                        : "rgba(26, 151, 245, 0.12)"
                      : isDark
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.08)",
                    color: isActive
                      ? isDark
                        ? "#ffffff"
                        : theme.palette.primary.main
                      : "inherit",
                  }}
                >
                  {t.count}
                </Box>
              )}
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};
